// 安全的日志工具
// 在生产环境中自动脱敏敏感信息

const isDevelopment = process.env.NODE_ENV === 'development';

// 敏感字段列表
const SENSITIVE_KEYS = [
  'token',
  'access_token',
  'password',
  'id',
  'email',
  'username',
  'tokenFull',
  'tokenPreview',
  'tokenLength',
  'authorization',
  'api_url',
  'api_base_url',
  'react_app_api_url',
  'backend_url',
  'backend',
  'url',
  'ip',
  'address',
];

// 检查是否是IP地址或包含IP的URL
function isIPOrURLWithIP(value: string): boolean {
  if (typeof value !== 'string') return false;
  
  // 检查是否包含IP地址（IPv4格式：xxx.xxx.xxx.xxx）
  const ipPattern = /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/;
  return ipPattern.test(value);
}

// 脱敏函数：移除或掩码敏感信息
function sanitizeData(data: any): any {
  if (!data || typeof data !== 'object') {
    // 如果是字符串，检查是否包含IP地址
    if (typeof data === 'string' && isIPOrURLWithIP(data)) {
      return '[REDACTED_URL]';
    }
    return data;
  }

  // 如果是数组，递归处理每个元素
  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item));
  }

  // 如果是对象，处理每个属性
  const sanitized: any = { ...data };

  for (const key of Object.keys(sanitized)) {
    const lowerKey = key.toLowerCase();

    // 检查是否是敏感字段
    const isSensitive = SENSITIVE_KEYS.some(
      sensitiveKey => lowerKey.includes(sensitiveKey.toLowerCase())
    );

    if (isSensitive) {
      // 敏感字段：显示类型和长度，但不显示内容
      if (typeof sanitized[key] === 'string' && sanitized[key].length > 0) {
        sanitized[key] = '[REDACTED]';
      } else if (typeof sanitized[key] === 'number') {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeData(sanitized[key]);
      }
    } else {
      // 非敏感字段：检查值是否包含IP地址
      if (typeof sanitized[key] === 'string' && isIPOrURLWithIP(sanitized[key])) {
        sanitized[key] = '[REDACTED_URL]';
      } else {
        // 递归处理嵌套对象
      sanitized[key] = sanitizeData(sanitized[key]);
      }
    }
  }

  return sanitized;
}

// 安全的日志工具
export const logger = {
  // 开发环境：完整输出；生产环境：脱敏输出
  log: (label: string, data?: any) => {
    if (isDevelopment) {
      console.log(label, data);
    } else if (data !== undefined) {
      // 生产环境：脱敏后输出
      const sanitized = sanitizeData(data);
      console.log(label, sanitized);
    } else {
      console.log(label);
    }
  },

  // 错误日志：始终输出，但脱敏敏感信息
  error: (label: string, data?: any) => {
    if (data !== undefined) {
      const sanitized = sanitizeData(data);
      console.error(label, sanitized);
    } else {
      console.error(label);
    }
  },

  // 警告日志：开发环境完整输出，生产环境脱敏
  warn: (label: string, data?: any) => {
    if (isDevelopment) {
      console.warn(label, data);
    } else if (data !== undefined) {
      const sanitized = sanitizeData(data);
      console.warn(label, sanitized);
    } else {
      console.warn(label);
    }
  },

  // 调试日志：只在开发环境输出
  debug: (label: string, data?: any) => {
    if (isDevelopment) {
      console.log(`🔍 [DEBUG] ${label}`, data);
    }
  },

  // 安全日志：始终脱敏，无论环境
  safeLog: (label: string, data?: any) => {
    if (data !== undefined) {
      const sanitized = sanitizeData(data);
      console.log(label, sanitized);
    } else {
      console.log(label);
    }
  },
};

export default logger;

